/**
 * Deliberately loose. The address is checked properly where it can be — by the backend's
 * `@Email`, and ultimately by whether the invitation mail arrives — so the job here is only to
 * catch the obvious typo before a round trip, not to relitigate RFC 5322.
 */
export function isEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
}

/** What the realm's password policy demands, mirrored so the user is told before the round trip. */
export const PASSWORD_MIN_LENGTH = 8

export function meetsPasswordPolicy(value: string): boolean {
  return (
    value.length >= PASSWORD_MIN_LENGTH &&
    /[a-z]/.test(value) &&
    /[A-Z]/.test(value) &&
    /\d/.test(value) &&
    /[^A-Za-z0-9]/.test(value)
  )
}

const LOWER = 'abcdefghijkmnopqrstuvwxyz'
const UPPER = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
const DIGITS = '23456789'
const SPECIAL = '!@#$%&*?+-='

/**
 * A password that satisfies the realm policy by construction, so an administrator handing one over
 * does not have to invent one — and does not reach for a memorable one they will reuse.
 *
 * `crypto.getRandomValues`, not `Math.random`: this ends up being somebody's password, however
 * briefly. Look-alike characters (l/1/I, O/0) are left out because these get read out loud.
 */
export function generatePassword(length = 16): string {
  const alphabet = LOWER + UPPER + DIGITS + SPECIAL
  // One of each class up front guarantees the policy; the shuffle keeps them from always
  // appearing in the same positions.
  const required = [pick(LOWER), pick(UPPER), pick(DIGITS), pick(SPECIAL)]
  const rest = Array.from({ length: Math.max(length, PASSWORD_MIN_LENGTH) - required.length }, () =>
    pick(alphabet),
  )
  return shuffle([...required, ...rest]).join('')
}

function pick(alphabet: string): string {
  return alphabet[randomBelow(alphabet.length)]
}

function shuffle<T>(items: T[]): T[] {
  for (let index = items.length - 1; index > 0; index--) {
    const swap = randomBelow(index + 1)
    ;[items[index], items[swap]] = [items[swap], items[index]]
  }
  return items
}

/**
 * Rejection sampling. Taking `random % bound` would make the first `2^32 % bound` values very
 * slightly more likely — irrelevant at these sizes, but not worth being wrong about in a
 * password generator.
 */
function randomBelow(bound: number): number {
  const limit = Math.floor(0xffffffff / bound) * bound
  const buffer = new Uint32Array(1)
  let value: number
  do {
    crypto.getRandomValues(buffer)
    value = buffer[0]
  } while (value >= limit)
  return value % bound
}
